import React, { useEffect, useMemo, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import cx from 'classnames';
import parse from 'html-react-parser';

import PlaySmallIcon from '../../assets/icons/PlaySmallIcon';
import { HEADER_IMG_SRC_SET } from '../../const';
import Modal, { Modals } from '../../modals/Modal';
import { generateRandomImageUrl, isUndefined, parseHighlightedText } from '../../utils/helpers';
import IKImage from '../IKImage/IKImage';
import { PreloadImageLink } from '../PreloadImageLink';
import { Button } from '../UI/Button';
import { IconButton } from '../UI/IconButton';
import { BottomItem } from './components/BottomItem';
import useStyles from './styles';

interface IntroBlockProps {
  data: any;
  video?: boolean;
  className?: string;
  blockClass?: string;
  headerClass?: string;
  descriptionClass?: string;
  isSmall?: boolean;
  hasGradient?: boolean;

  [x: string]: any;
}

const IntroBlock: React.FC<IntroBlockProps> = ({
  data: introData,
  video = true,
  className = '',
  blockClass = '',
  headerClass = '',
  descriptionClass = '',
  isSmall = undefined,
  hasGradient = undefined,
  tenant,
}) => {
  const background = introData?.background;
  const highlightColor = introData?.heading_highlight_color;
  const classes = useStyles(background, video, highlightColor)();
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const [isVideoVisible, setVideoVisibility] = useState(false);
  const playBtnTitle = introData?.watch_video_button_text;
  const bottomItems = introData?.usp_blocks;

  useEffect(() => {
    const previewVideo = previewVideoRef.current;

    if (previewVideo) {
      previewVideo.oncanplay = () => {
        setVideoVisibility(true);
      };

      previewVideo.onended = () => {
        setVideoVisibility(false);
      };
    }

    return () => {
      if (previewVideo) {
        previewVideo.onended = null;
        previewVideo.oncanplay = null;
      }
    };
  }, []);

  const heading =
    typeof introData?.heading === 'string' ? introData?.heading : introData?.heading?.en;
  const description =
    typeof introData?.description === 'string'
      ? introData?.description
      : introData?.description?.en;
  const previewImg = useMemo(
    () => introData?.preview_image_url || generateRandomImageUrl(tenant?.name),
    [tenant, introData],
  );
  const buttonText = introData?.button_text;
  const buttonUrl = introData?.button_url;
  const videoUrl = introData?.video_url || '';
  const previewVideoUrl = introData?.preview_video_url;
  const gradient = isUndefined(hasGradient) ? introData?.gradient : hasGradient;

  const isImageBlockOnlyWithHeading = isUndefined(isSmall)
    ? !video && !buttonText && (description || heading)
    : isSmall;

  return (
    <>
      {previewImg && <PreloadImageLink path={previewImg} />}
      <Box data-testid="introBlock" className={cx('intro-block', classes.wrapper, className)}>
        <IKImage
          className={classes.img}
          path={previewImg}
          alt={heading}
          srcset={HEADER_IMG_SRC_SET}
          sizes="100vw"
          draggable={false}
        />
        <video
          ref={previewVideoRef}
          className={cx(classes.previewVideo, !isVideoVisible && classes.hide)}
          autoPlay
          muted
          src={previewVideoUrl}
        />
        <Container
          className={cx(
            classes.container,
            isImageBlockOnlyWithHeading && classes.smallPadding,
            'intro-block-container',
          )}
        >
          <div className={cx(classes.textWrapper, blockClass)}>
            {heading && (
              <Typography
                variant="h1"
                component="h1"
                align="center"
                className={cx(
                  classes.title,
                  headerClass,
                  isImageBlockOnlyWithHeading && classes.smallMargin,
                )}
              >
                {parse(parseHighlightedText(heading))}
              </Typography>
            )}

            {description && (
              <Typography align="center" className={cx(classes.subTitle, descriptionClass)}>
                {description}
              </Typography>
            )}

            {video && (
              <Modal url={videoUrl} bumperUrl={null}>
                {({ openModal }) => (
                  <IconButton
                    btnClass={classes.playBtn}
                    icon={<PlaySmallIcon color={'currentColor'} className={classes.playIcon} />}
                    label={playBtnTitle}
                    onClick={() => openModal(Modals.VideoModal)}
                    size="large"
                  />
                )}
              </Modal>
            )}

            {buttonText && (
              <Button className={classes.customBtn} text={buttonText} link={buttonUrl} />
            )}
          </div>
        </Container>

        {gradient && <div className={classes.gradient} />}

        {/* Only for Intro Image block desktop version */}

        {!video && <div className={classes.topGradient} />}
        <div className={classes.bottomGradient} />
      </Box>
      {bottomItems?.length > 0 && (
        <Container className={cx(classes.bottomItemsWrapper, 'dark-block')}>
          {bottomItems.map((item) => (
            <BottomItem key={item?.image} title={item?.label_text} imageUrl={item?.image} />
          ))}
        </Container>
      )}
    </>
  );
};

export default IntroBlock;
